// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Button,
  CircularProgress,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Document } from '@shared/data-access/types/engine-types';
import { AppIcon, ShowInAppClient } from '@shared/ui-kit';
import {
  IconButton,
  type IconButtonProps,
} from '@shared/ui-kit/components/icon-button';
import { Input } from '@shared/ui-kit/components/input';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { ReactNode } from 'react';
import { DocumentFileListItem } from '../document-file-list-item/document-file-list-item';
import type { LinkedDocumentFileRow } from '../entity-documents-section/entity-documents-section.utils';
import { EntityDocumentsLinkPanel } from '../entity-documents-link-panel';
import { UploadDocumentDrawer } from '../upload-document-drawer/upload-document-drawer';
import {
  useEntityDocumentsTab,
  type EntityDocumentsTabProps,
} from './use-entity-documents-tab';

export type { EntityDocumentsTabProps };

const ActionIconButton = ({
  tooltip,
  ...props
}: IconButtonProps & { readonly tooltip: string }) => (
  <Tooltip title={tooltip} placement="top">
    <span className="inline-flex shrink-0 items-center justify-center">
      <IconButton {...props} />
    </span>
  </Tooltip>
);

const EmptyDocsState = ({
  titleKey,
  descriptionKey,
  action,
}: {
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly action?: ReactNode;
}) => (
  <div
    className="flex flex-col items-center gap-3 rounded-lg px-4 py-10 text-center"
    data-testid="entity-docs-tab-empty"
  >
    <span className="flex size-14 items-center justify-center rounded-xl bg-page">
      <AppIcon icon="FileText" size="xl" color="disabled" />
    </span>
    <Typography
      component="p"
      variant="subtitle1"
      className="!mb-0 !font-semibold !text-title-dark"
    >
      <Translation i18nKey={titleKey as any} ns="components" />
    </Typography>
    <Typography
      component="p"
      variant="body2"
      className="!mb-0 max-w-xs !text-primary-medium"
    >
      <Translation i18nKey={descriptionKey as any} ns="components" />
    </Typography>
    {action ?? null}
  </div>
);

const DocumentGroupHeader = ({
  title,
  fileCount,
  action,
}: {
  readonly title: string;
  readonly fileCount: number;
  readonly action?: ReactNode;
}) => (
  <header className="flex items-center gap-3">
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <p className="truncate text-base font-medium text-title-dark" title={title}>
        {title}
      </p>
      <p className="text-sm text-primary-medium">
        <Translation
          i18nKey="documents.file-count"
          ns="components"
          count={fileCount}
        />
      </p>
    </div>
    {action ?? null}
  </header>
);

const DocumentGroupFiles = ({
  fileRows,
  downloadingRowKey,
  t,
  onDownloadFile,
}: {
  readonly fileRows: readonly LinkedDocumentFileRow[];
  readonly downloadingRowKey: string | null;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly onDownloadFile: (row: LinkedDocumentFileRow) => void;
}) => {
  if (fileRows.length === 0) {
    return (
      <p className="text-sm text-primary-medium">
        <Translation
          i18nKey="documents.entity-section.files-empty"
          ns="components"
        />
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {fileRows.map(row => {
        const isDownloading = downloadingRowKey === row.rowKey;
        const downloadLabel = t('documents.entity-section.download-aria', {
          ns: 'components',
          fileName: row.fileName,
        });

        return (
          <DocumentFileListItem
            key={row.rowKey}
            fileName={row.fileName}
            fileSize={row.fileSize}
            surface="section"
            testId={`entity-docs-file-${row.rowKey}`}
            action={
              isDownloading ? (
                <CircularProgress size={20} />
              ) : (
                <ActionIconButton
                  icon="Download"
                  variant="icon"
                  size="lg"
                  color="main"
                  tooltip={downloadLabel}
                  aria-label={downloadLabel}
                  data-testid={`entity-docs-download-${row.rowKey}`}
                  onClick={() => onDownloadFile(row)}
                />
              )
            }
          />
        );
      })}
    </ul>
  );
};

const DocumentGroup = ({
  document,
  fileRows,
  fileCount,
  showUnlinkAction,
  downloadingRowKey,
  t,
  onUnlinkDocument,
  onDownloadFile,
}: {
  readonly document: Document;
  readonly fileRows: readonly LinkedDocumentFileRow[];
  readonly fileCount: number;
  readonly showUnlinkAction: boolean;
  readonly downloadingRowKey: string | null;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly onUnlinkDocument: (document: Document) => void;
  readonly onDownloadFile: (row: LinkedDocumentFileRow) => void;
}) => {
  const unlinkLabel = t('documents.entity-section.unlink-aria', {
    ns: 'components',
    title: document.title,
  });

  return (
    <li
      className="flex flex-col gap-2"
      data-testid={`entity-docs-document-${document.id}`}
    >
      <DocumentGroupHeader
        title={document.title}
        fileCount={fileCount}
        action={
          showUnlinkAction ? (
            <ShowInAppClient>
              <ActionIconButton
                icon="Trashcan"
                variant="icon"
                size="lg"
                color="dark"
                tooltip={unlinkLabel}
                aria-label={unlinkLabel}
                data-testid={`entity-docs-unlink-${document.id}`}
                onClick={() => onUnlinkDocument(document)}
                className="!mr-3"
              />
            </ShowInAppClient>
          ) : undefined
        }
      />
      <DocumentGroupFiles
        fileRows={fileRows}
        downloadingRowKey={downloadingRowKey}
        t={t}
        onDownloadFile={onDownloadFile}
      />
      <Divider className="!my-4" />
    </li>
  );
};

export const EntityDocumentsTab = defineComponent({
  displayName: 'EntityDocumentsTab',
  useSetup: useEntityDocumentsTab,
  View: ({
    t,
    entityType,
    entityId,
    linkedDocuments,
    searchQuery,
    isUploadOpen,
    downloadingRowKey,
    linkingDocumentId,
    isCatalogLoading,
    showEmptyNoDocuments,
    showEmptyNoResults,
    canUpload,
    showUnlinkAction,
    showLinkPanel,
    hasRelatableDocuments,
    showRelatableSearch,
    relatableSearchQuery,
    relatableDocuments,
    onSearchQueryChange,
    onRelatableSearchQueryChange,
    onOpenUpload,
    onCloseUpload,
    onLinkDocument,
    onUnlinkDocument,
    onDownloadFile,
    buildFileRowsForDocument,
  }) => (
    <div className="flex w-full flex-col gap-5" data-testid="entity-documents-tab">
      <div className="flex items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <AppIcon icon="Search" color="disabled" size="base" />
          </span>
          <Input
            type="search"
            autoComplete="off"
            className="h-10 w-full pl-10"
            placeholder={t('documents.entity-docs-tab.search-placeholder', {
              ns: 'components',
            })}
            value={searchQuery}
            onChange={onSearchQueryChange}
            data-testid="entity-docs-search"
          />
        </div>
        {canUpload ? (
          <ShowInAppClient>
            <Button
              type="button"
              variant="contained"
              color="secondary"
              startIcon={<AppIcon icon="Plus" color="light" size="base" />}
              onClick={onOpenUpload}
              className="h-10 shrink-0 normal-case"
              data-testid="entity-docs-upload-button"
            >
              <Translation
                i18nKey="documents.entity-docs-tab.upload"
                ns="components"
              />
            </Button>
          </ShowInAppClient>
        ) : null}
      </div>

      {isCatalogLoading ? (
        <div className="flex items-center gap-2 py-1">
          <CircularProgress size={18} />
          <Typography
            component="span"
            variant="body2"
            className="text-primary-medium"
          >
            <Translation
              i18nKey="documents.entity-section.loading"
              ns="components"
            />
          </Typography>
        </div>
      ) : null}

      {showEmptyNoDocuments ? (
        <EmptyDocsState
          titleKey="documents.entity-docs-tab.empty-title"
          descriptionKey="documents.entity-docs-tab.empty-description"
          action={
            canUpload ? (
              <ShowInAppClient>
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  startIcon={<AppIcon icon="Plus" color="light" size="base" />}
                  onClick={onOpenUpload}
                  className="h-10 normal-case"
                  data-testid="entity-docs-empty-upload-button"
                >
                  <Translation
                    i18nKey="documents.entity-docs-tab.upload-document"
                    ns="components"
                  />
                </Button>
              </ShowInAppClient>
            ) : undefined
          }
        />
      ) : null}

      {showEmptyNoResults ? (
        <EmptyDocsState
          titleKey="documents.entity-docs-tab.no-results-title"
          descriptionKey="documents.entity-docs-tab.no-results-description"
        />
      ) : null}

      {!isCatalogLoading && linkedDocuments.length > 0 ? (
        <ul className="flex flex-col gap-4" data-testid="entity-docs-list">
          {linkedDocuments.map(document => {
            const fileRows = buildFileRowsForDocument(document);
            const fileCount = document.files?.length ?? 0;

            return (
              <DocumentGroup
                key={document.id}
                document={document}
                fileRows={fileRows}
                fileCount={fileCount}
                showUnlinkAction={showUnlinkAction}
                downloadingRowKey={downloadingRowKey}
                t={t}
                onUnlinkDocument={onUnlinkDocument}
                onDownloadFile={row => void onDownloadFile(row)}
              />
            );
          })}
        </ul>
      ) : null}

      <EntityDocumentsLinkPanel
        t={t}
        showLinkPanel={showLinkPanel}
        hasRelatableDocuments={hasRelatableDocuments}
        showRelatableSearch={showRelatableSearch}
        relatableSearchQuery={relatableSearchQuery}
        onRelatableSearchQueryChange={onRelatableSearchQueryChange}
        relatableDocuments={relatableDocuments}
        linkingDocumentId={linkingDocumentId}
        onLinkDocument={onLinkDocument}
      />

      {canUpload ? (
        <UploadDocumentDrawer
          open={isUploadOpen}
          onClose={onCloseUpload}
          linkToEntity={{ entityType, entityId }}
          hideBackdrop
        />
      ) : null}
    </div>
  ),
});
